import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categoryIndexPartA } from "@/mocks/categoryIndexPartA";
import { categoryIndexPartB } from "@/mocks/categoryIndexPartB";

export const atoZLetters: string[] = [...categoryIndexPartA, ...categoryIndexPartB].map(
  (group) => group.letter,
);

export default function AtoZDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = location.pathname.startsWith("/categories-a-z");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap px-1 py-2 text-sm font-medium transition-colors duration-200 ${
          isActive || open ? "text-primary-700" : "text-foreground-700 hover:text-primary-700"
        }`}
      >
        A to Z Categories
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={open ? "ri-arrow-up-s-line text-base" : "ri-arrow-down-s-line text-base"} />
        </span>
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-[440px] overflow-hidden rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-sort-alphabet-asc text-lg" />
            </span>
            <div>
              <p className="font-heading text-sm font-bold text-foreground-950">
                Jump to a letter
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                Every business category on Coastal Bizz Connect, A to Z.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-7 gap-1.5">
            {atoZLetters.map((letter) => (
              <Link
                key={letter}
                to={`/categories-a-z#letter-${letter}`}
                onClick={() => setOpen(false)}
                aria-label={`Categories starting with ${letter}`}
                className="flex h-9 items-center justify-center rounded-md bg-background-100 text-xs font-bold uppercase text-foreground-700 transition-colors duration-200 hover:bg-secondary-100 hover:text-secondary-900"
              >
                {letter}
              </Link>
            ))}
          </div>

          <div className="mt-4 border-t border-background-200 pt-3">
            <Link
              to="/categories-a-z"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-primary-700 transition-colors hover:text-primary-800"
            >
              Browse the full A to Z index
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}